import { callFailureText, isCrmWindowMessage, type Me } from '@smarton-pbx/shared';
import { type CallInfo, Softphone } from '@smarton-pbx/softphone';
import { Grid3x3, Pause, Phone, PhoneOff, Play } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { message, t, tc } from '../i18n';
import { notificationState } from '../lib/permissions';
import { ensurePushSubscription } from '../lib/push';
import { ringer } from '../lib/ringer';
import { CallerPanel } from './CallerPanel';

export type PhoneStatus = 'starting' | 'other-tab' | 'connecting' | 'registered' | 'failed';

interface PhoneState {
  status: PhoneStatus;
  detail: string | undefined;
  /** The last call, kept when it has ended (to say how). */
  call: CallInfo | null;
  /** A call is ringing or going on (an ended one doesn't count): no new call now. */
  busy: boolean;
  dial: (destination: string) => void;
  answer: () => void;
  hangup: () => void;
  /** Keys pressed during a call (a menu's choices). */
  sendKeys: (keys: string) => void;
  /** What SmartOn asked this tab for went to the tab with the phone. */
  handedOver: string | null;
  /** Why the last call out didn't go through (refused, or not put through by the phone company). */
  failure: string | null;
  clearFailure: () => void;
}

const PhoneContext = createContext<PhoneState | null>(null);

export function usePhone(): PhoneState {
  const state = useContext(PhoneContext);
  if (!state) throw new Error('usePhone outside SoftphoneProvider');
  return state;
}

const DEVICE_KEY = 'softphone-device-id';
const params = new URLSearchParams(window.location.search);
// The automated audio test turns off the browser's audio processing so its
// test tones arrive unchanged, and reads the remote stream.
const e2eMode = params.has('e2e');
const sipDebug = params.has('sipdebug');
// Tests: every call's audio through the TURN relay.
const relayOnly = e2eMode && params.has('relay');

/**
 * A call to pick up as soon as it rings here: "Answer" in a notification
 * opens the app with ?answer=<call ID> (or tells an open app). The app then
 * connects, and Asterisk delivers the call it has been holding.
 */
let pendingAnswer: { callId: string; until: number } | null = null;
const ANSWER_WITHIN_MS = 45_000;
const listeners = new Set<() => void>();

export function answerWhenRinging(callId: string): void {
  pendingAnswer = { callId, until: Date.now() + ANSWER_WITHIN_MS };
  for (const listener of listeners) listener();
}

/**
 * A call SmartOn asked for: it opens the app with ?call=<number> (and
 * &saleEvent=<id> from a sales event) or tells the PBX window it opened. The
 * number is dialled as soon as the phone is connected.
 */
let pendingCall: { number: string; saleEventId?: number; until: number } | null = null;
const CALL_WITHIN_MS = 60_000;

export function callWhenReady(number: string, saleEventId?: number): void {
  const destination = number.replace(/[\s()./-]/g, '');
  if (!/^\+?[0-9*#]{2,32}$/.test(destination)) return;
  pendingCall = { number: destination, ...(saleEventId && { saleEventId }), until: Date.now() + CALL_WITHIN_MS };
  for (const listener of listeners) listener();
}

if (params.has('answer')) answerWhenRinging(params.get('answer')!);
if (params.has('call')) {
  const saleEventId = Number(params.get('saleEvent'));
  callWhenReady(params.get('call')!, Number.isInteger(saleEventId) && saleEventId > 0 ? saleEventId : undefined);
}
if (params.has('answer') || params.has('call')) {
  for (const name of ['answer', 'call', 'saleEvent']) params.delete(name);
  const rest = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
}

/**
 * Only one tab runs the phone. A tab asked to call or answer while another
 * has the phone passes the request on to it.
 */
type PhoneRequest = { action: 'call'; number: string; saleEventId?: number } | { action: 'answer'; callId: string };
const requests = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('softphone-requests');
let holdsPhone = false;
/** How long a new tab waits for the phone before handing a request to the tab that has it. */
const HAND_OVER_AFTER_MS = 1500;

function take(request: PhoneRequest): void {
  if (request.action === 'answer') answerWhenRinging(request.callId);
  else callWhenReady(request.number, request.saleEventId);
}

requests?.addEventListener('message', (event: MessageEvent<PhoneRequest>) => {
  if (holdsPhone) take(event.data);
});

declare global {
  interface Window {
    __softphone?: Softphone;
  }
}

/** A readable name for this browser, shown to admins in the device list. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

/**
 * Runs the softphone for the signed-in user while the app is open. Only one
 * tab per browser runs it (two would both ring and both send audio): tabs
 * take turns through a Web Lock, and when the active tab closes the next
 * one takes over.
 */
export function SoftphoneProvider({
  userId,
  productName,
  iconUrl,
  crmUrl,
  children,
}: {
  /** The signed-in person: whose calls out are looked up when one didn't go through. */
  userId: string;
  /** For notifications: always the tenant's name, never ours. */
  productName: string;
  iconUrl: string | null;
  /** The company's SmartOn, which may ask this window to call or answer. */
  crmUrl: string | null;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<PhoneStatus>('starting');
  const [detail, setDetail] = useState<string>();
  const [call, setCall] = useState<CallInfo | null>(null);
  const phoneRef = useRef<Softphone | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const stop = new AbortController();
    const stopped = new Promise<void>((resolve) => stop.signal.addEventListener('abort', () => resolve()));

    async function run(): Promise<void> {
      setStatus('connecting');
      try {
        const credentials = await api.softphoneCredentials({
          deviceId: localStorage.getItem(DEVICE_KEY),
          label: deviceLabel(),
        });
        localStorage.setItem(DEVICE_KEY, credentials.deviceId);
        if (stop.signal.aborted) return;
        // Notifications while the app is closed go to this browser's device.
        void ensurePushSubscription(credentials.pushKey);

        const phone = new Softphone({
          websocketUrl: credentials.websocketUrl,
          domain: credentials.domain,
          endpointId: credentials.endpointId,
          password: credentials.password,
          iceServers: credentials.iceServers,
          ...(relayOnly && { iceTransportPolicy: 'relay' as const }),
          debug: sipDebug,
          ...(e2eMode && { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }),
        });
        phone.on('registration', (state, why) => {
          setStatus(state === 'registered' ? 'registered' : state === 'failed' ? 'failed' : 'connecting');
          setDetail(why === 'connection lost' ? t('connection lost') : why);
        });
        phone.on('call', setCall);
        phone.on('remoteStream', (stream) => {
          if (audioRef.current) audioRef.current.srcObject = stream;
        });
        phoneRef.current = phone;
        if (e2eMode) window.__softphone = phone;
        holdsPhone = true;
        phone.start();
        await stopped;
        holdsPhone = false;
        phone.stop();
        phoneRef.current = null;
      } catch (error) {
        setStatus('failed');
        setDetail(error instanceof Error ? error.message : String(error));
        await stopped;
      }
    }

    if ('locks' in navigator) {
      setStatus('other-tab');
      navigator.locks.request('softphone', { signal: stop.signal }, run).catch(() => undefined);
    } else {
      void run();
    }
    return () => stop.abort();
  }, []);

  // Answer a call the person already chose to answer from a notification.
  const [answerRequests, setAnswerRequests] = useState(0);
  useEffect(() => {
    const listener = () => setAnswerRequests((n) => n + 1);
    listeners.add(listener);
    return () => void listeners.delete(listener);
  }, []);
  useEffect(() => {
    if (call?.state !== 'ringing-in' || !call.callId || !pendingAnswer) return;
    if (pendingAnswer.callId === call.callId && Date.now() < pendingAnswer.until) {
      pendingAnswer = null;
      phoneRef.current?.answer();
    }
  }, [call?.state, call?.callId, answerRequests]);

  // Dial what SmartOn asked for, once connected and free (from a sales event: the call goes into it).
  const busy = call !== null && call.state !== 'ended';
  useEffect(() => {
    if (status !== 'registered' || busy || !pendingCall) return;
    const request = pendingCall;
    pendingCall = null;
    if (Date.now() > request.until) return;
    const intent = request.saleEventId ? api.crmCallIntent({ number: request.number, saleEventId: request.saleEventId }).catch(() => undefined) : Promise.resolve();
    void intent.then(() => phoneRef.current?.dial(request.number));
  }, [status, busy, answerRequests]);

  // Another tab has the phone: what SmartOn asked this one for goes there.
  const [handedOver, setHandedOver] = useState<string | null>(null);
  useEffect(() => {
    if (status !== 'other-tab' || !requests || (!pendingCall && !pendingAnswer)) return;
    const timer = setTimeout(() => {
      if (pendingAnswer) {
        requests.postMessage({ action: 'answer', callId: pendingAnswer.callId } satisfies PhoneRequest);
        pendingAnswer = null;
        setHandedOver(t('The call is answered in your other tab.'));
      }
      if (pendingCall) {
        requests.postMessage({ action: 'call', number: pendingCall.number, ...(pendingCall.saleEventId && { saleEventId: pendingCall.saleEventId }) } satisfies PhoneRequest);
        setHandedOver(t('Calling {number} from your other tab.', { number: pendingCall.number }));
        pendingCall = null;
      }
    }, HAND_OVER_AFTER_MS);
    return () => clearTimeout(timer);
  }, [status, answerRequests]);

  // SmartOn tells a PBX window it opened to call or answer (instead of opening it again over a call).
  useEffect(() => {
    if (!crmUrl) return;
    const origin = new URL(crmUrl).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || !isCrmWindowMessage(event.data)) return;
      const message = event.data;
      setHandedOver(null);
      take(message.action === 'answer' ? { action: 'answer', callId: message.callId } : { action: 'call', number: message.number, saleEventId: message.saleEventId });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [crmUrl]);

  // Sound and notifications for the call's state.
  useEffect(() => {
    if (call?.state === 'ringing-out') {
      ringer.startRingback();
      return () => ringer.stop();
    }
    if (call?.state !== 'ringing-in') return;
    ringer.start();
    let notification: Notification | undefined;
    if (document.visibilityState === 'hidden' && notificationState() === 'granted') {
      try {
        notification = new Notification(t('Incoming call from {number}', { number: call.remote }), {
          body: productName,
          tag: 'incoming-call',
          requireInteraction: true,
          ...(iconUrl && { icon: iconUrl }),
        });
        notification.onclick = () => {
          window.focus();
          notification?.close();
        };
      } catch {
        // Some browsers (Chrome on Android) only allow notifications from a
        // service worker; those arrive with web push in milestone M4.
      }
    }
    return () => {
      ringer.stop();
      notification?.close();
    };
  }, [call?.state, call?.remote, productName, iconUrl]);

  // A call out that didn't go through: why, from the call's record (the phone system's refusal,
  // or the phone company's cause). The PBX finishes the record a moment after the call ends here.
  const [failure, setFailure] = useState<string | null>(null);
  const outSince = useRef(0);
  useEffect(() => {
    if (call?.direction !== 'outgoing') return;
    if (call.state === 'ringing-out') {
      outSince.current ||= Date.now();
      setFailure(null);
      return;
    }
    if (call.state !== 'ended' || !outSince.current) return;
    const since = outSince.current;
    outSince.current = 0;
    let stopped = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = () =>
      api.listCalls({ userId, limit: 1 }).then(
        (page) => {
          const last = page.items[0];
          if (stopped || !last || Date.parse(last.startedAt) < since - 5000) return;
          if (last.outcome === null && ++tries < 5) {
            timer = setTimeout(look, 800);
            return;
          }
          setFailure(callFailureText(last));
        },
        () => undefined,
      );
    timer = setTimeout(look, 300);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [call?.direction, call?.state, userId]);

  const state: PhoneState = {
    status,
    detail,
    call,
    busy,
    dial: (destination) => phoneRef.current?.dial(destination),
    answer: () => phoneRef.current?.answer(),
    hangup: () => {
      // Declining stops the call ringing on the person's other devices too (as the app's Decline does).
      if (call?.state === 'ringing-in' && call.callId) void api.declineCall(call.callId).catch(() => undefined);
      phoneRef.current?.hangup();
    },
    sendKeys: (keys) => phoneRef.current?.sendKeys(keys),
    handedOver,
    failure,
    clearFailure: () => setFailure(null),
  };

  return (
    <PhoneContext.Provider value={state}>
      {children}
      <audio ref={audioRef} autoPlay />
    </PhoneContext.Provider>
  );
}

/**
 * Whether the call in progress is being recorded, and pausing it (e.g. while
 * a customer reads out a card number). Asterisk reports the recording a
 * moment after the call connects, so this asks a few times.
 */
function useRecordingControl(connected: boolean) {
  const [recording, setRecording] = useState<{ paused: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setRecording(null);
    setError(undefined);
    if (!connected) return;
    let cancelled = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () =>
      api.activeCall().then(
        (active) => {
          if (cancelled) return;
          if (active) setRecording({ paused: active.paused });
          else if (++tries < 6) timer = setTimeout(check, 1000);
        },
        () => undefined,
      );
    timer = setTimeout(check, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [connected]);

  async function toggle() {
    if (!recording) return;
    setBusy(true);
    setError(undefined);
    try {
      setRecording({ paused: (await api.setRecordingPaused(!recording.paused)).paused });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return { recording, busy, error, toggle };
}

/** Floats over every page while a call rings or is in progress. */
export function CallCard({ me }: { me: Me }) {
  const { call, answer, hangup, sendKeys, failure, clearFailure } = usePhone();
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [keypad, setKeypad] = useState(false);
  const [pressed, setPressed] = useState('');
  const [now, setNow] = useState(Date.now());
  const control = useRecordingControl(call?.state === 'connected');

  useEffect(() => {
    if (call?.state !== 'connected') {
      setConnectedAt(null);
      return;
    }
    setConnectedAt((at) => at ?? Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [call?.state]);

  useEffect(() => {
    if (call?.state === 'connected') return;
    setKeypad(false);
    setPressed('');
  }, [call?.state]);

  // With the keypad open, the keyboard's digits, * and # press keys too.
  useEffect(() => {
    if (!keypad) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (/^[0-9*#]$/.test(event.key)) press(event.key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const press = (key: string) => {
    sendKeys(key);
    setPressed((keys) => (keys + key).slice(-16));
  };

  if (!call || call.state === 'ended') {
    if (!failure) return null;
    // A call out that didn't go through: why, until it's closed or the next call starts.
    return (
      <div className="call-card" role="alert" data-testid="call-refused">
        <div className="caller">
          <span className="avatar large" aria-hidden>
            <PhoneOff size={22} />
          </span>
          <div>
            <div className="label">{t('Call not put through')}</div>
            {call && <div className="number tabular">{call.remoteName ?? call.remote}</div>}
          </div>
        </div>
        <p style={{ margin: 0 }}>{message(failure)}</p>
        <div className="call-actions">
          <button type="button" className="secondary" onClick={clearFailure} data-testid="close-refusal">
            {t('Close')}
          </button>
        </div>
      </div>
    );
  }

  const label = call.state === 'ringing-in' ? t('Incoming call') : call.state === 'ringing-out' ? t('Calling…') : t('On a call');
  const elapsed = connectedAt ? Math.max(0, Math.floor((now - connectedAt) / 1000)) : 0;
  const duration = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div className="call-card" role="status" aria-live="assertive" data-testid="call-banner">
      <div className="caller">
        <span className={`avatar large ${call.state === 'ringing-in' ? 'ringing' : ''}`} aria-hidden>
          <Phone size={22} />
        </span>
        <div>
          <div className="label">{label}</div>
          <div className="number tabular">{call.remoteName ?? call.remote}</div>
          {call.remoteName && <div className="muted tabular">{call.remote}</div>}
          {call.state === 'connected' && <div className="muted tabular">{duration}</div>}
        </div>
      </div>
      <CallerPanel me={me} call={call} />
      {me.recordingNotice === 'REMIND' && call.state !== 'ringing-in' && (
        <div className="notice-reminder" data-testid="recording-reminder">
          {me.recordingNoticeText ? t('Tell them: “{text}”', { text: me.recordingNoticeText }) : t('Remember to tell them this call is recorded.')}
        </div>
      )}
      {call.state === 'connected' && control.recording && (
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className={`recording-state ${control.recording.paused ? 'paused' : ''}`} data-testid="recording-state" data-paused={control.recording.paused}>
            <span className="dot" aria-hidden />
            {control.recording.paused ? t('Recording paused') : tc('now', 'Recording')}
          </span>
          <button type="button" className="secondary small" onClick={() => void control.toggle()} disabled={control.busy} data-testid="pause-recording">
            {control.recording.paused ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
            {control.recording.paused ? t('Resume recording') : t('Pause recording')}
          </button>
        </div>
      )}
      {control.error && <div className="field-error">{control.error}</div>}
      {keypad && call.state === 'connected' && (
        <div className="keypad" data-testid="keypad">
          <div className="keypad-pressed tabular" aria-live="polite">{pressed || '\u00a0'}</div>
          <div className="keypad-keys">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => (
              <button key={key} type="button" className="secondary" onClick={() => press(key)} data-testid={`key-${key}`}>
                {key}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="call-actions">
        {call.state === 'connected' && (
          <button
            type="button"
            className={`round secondary ${keypad ? 'active' : ''}`}
            onClick={() => setKeypad((open) => !open)}
            aria-label={keypad ? t('Hide keypad') : t('Keypad')}
            aria-pressed={keypad}
            title={t('Keypad')}
            data-testid="keypad-toggle"
          >
            <Grid3x3 size={22} aria-hidden />
          </button>
        )}
        {call.state === 'ringing-in' && (
          <button type="button" className="round answer" onClick={answer} aria-label={t('Answer')} title={t('Answer')} data-testid="answer">
            <Phone size={22} aria-hidden />
          </button>
        )}
        <button
          type="button"
          className="round end"
          onClick={hangup}
          aria-label={call.state === 'ringing-in' ? t('Decline') : t('Hang up')}
          title={call.state === 'ringing-in' ? t('Decline') : t('Hang up')}
          data-testid="hangup"
        >
          <PhoneOff size={22} aria-hidden />
        </button>
      </div>
    </div>
  );
}

const statusText: Record<PhoneStatus, string> = {
  starting: t('Starting…'),
  'other-tab': t('Open in another tab'),
  connecting: t('Connecting…'),
  registered: t('Ready for calls'),
  failed: t('Not connected'),
};

export function phoneStatusText(status: PhoneStatus): string {
  return statusText[status];
}

/** The phone's state, in the sidebar. */
export function PhoneStatusChip() {
  const { status } = usePhone();
  const light = status === 'registered' ? 'on' : status === 'failed' ? 'off' : 'wait';
  return (
    <div className="status-chip">
      <span className={`status-light ${light}`} aria-hidden />
      {statusText[status]}
    </div>
  );
}
